import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  API_BASE_URL,
  API_ENDPOINTS,
  HealthResponse,
  StatusResponse,
  MetricsResponse,
  AnalysisRequest,
  AnalysisResponse,
  AIRoutingRequest,
  AIRoutingResponse,
  SuggestionRequest,
  SuggestionsResponse,
  APIError,
  ApiResponse
} from '@/types/api';

class APIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error: AxiosError) => {
        console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`, error.message);

        // Handle different error scenarios
        if (error.code === 'ERR_NETWORK') {
          console.warn('🌐 Network error - possibly DNS propagation issue');
        }

        if (error.response?.status === 429) {
          console.warn('⏱️ Rate limited - please try again later');
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): APIError {
    if (error.response) {
      // Server responded with error status
      return {
        error: error.response.data?.error || 'Server error',
        code: error.response.data?.code || 'SERVER_ERROR',
        details: error.response.data?.details,
      };
    } else if (error.request) {
      // Network error
      if (error.code === 'ERR_NETWORK') {
        return {
          error: 'Network error - unable to reach server',
          code: 'NETWORK_ERROR',
          details: 'This may be due to DNS propagation or server unavailability',
        };
      }
      return {
        error: 'Network error',
        code: 'NETWORK_ERROR',
        details: error.message,
      };
    } else {
      // Other error
      return {
        error: error.message || 'Unknown error',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  // Generic request method
  private async request<T>(
    method: 'GET' | 'POST',
    endpoint: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request({
        method,
        url: endpoint,
        data,
      });

      return {
        data: response.data,
        loading: false,
        status: response.status,
      };
    } catch (error) {
      return {
        error: error as APIError,
        loading: false,
        status: (error as any).response?.status || 0,
      };
    }
  }

  // Health check
  async getHealth(): Promise<ApiResponse<HealthResponse>> {
    return this.request<HealthResponse>('GET', API_ENDPOINTS.HEALTH);
  }

  // Service status
  async getStatus(): Promise<ApiResponse<StatusResponse>> {
    return this.request<StatusResponse>('GET', API_ENDPOINTS.STATUS);
  }

  // System metrics
  async getMetrics(): Promise<ApiResponse<MetricsResponse>> {
    return this.request<MetricsResponse>('GET', API_ENDPOINTS.METRICS);
  }

  // Project analysis
  async analyzeProject(request: AnalysisRequest): Promise<ApiResponse<AnalysisResponse>> {
    return this.request<AnalysisResponse>('POST', API_ENDPOINTS.ANALYZE, request);
  }

  // AI model routing
  async routeAIModel(request: AIRoutingRequest): Promise<ApiResponse<AIRoutingResponse>> {
    return this.request<AIRoutingResponse>('POST', API_ENDPOINTS.AI_ROUTE, request);
  }

  // Cross-language suggestions
  async getSuggestions(request: SuggestionRequest): Promise<ApiResponse<SuggestionsResponse>> {
    return this.request<SuggestionsResponse>('POST', API_ENDPOINTS.SUGGESTIONS, request);
  }

  // Check API availability
  async checkAvailability(): Promise<boolean> {
    try {
      const response = await this.client.get(API_ENDPOINTS.HEALTH, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      console.warn('API not available:', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }

  // Get API status summary
  async getStatusSummary(): Promise<{
    available: boolean;
    health?: HealthResponse;
    status?: StatusResponse;
    metrics?: MetricsResponse;
    errors: string[];
  }> {
    const errors: string[] = [];
    let health: HealthResponse | undefined;
    let status: StatusResponse | undefined;
    let metrics: MetricsResponse | undefined;

    try {
      const healthResult = await this.getHealth();
      if (healthResult.error) {
        errors.push(`Health check: ${healthResult.error.error}`);
      } else {
        health = healthResult.data;
      }
    } catch (error) {
      errors.push('Health check failed');
    }

    try {
      const statusResult = await this.getStatus();
      if (statusResult.error) {
        errors.push(`Status check: ${statusResult.error.error}`);
      } else {
        status = statusResult.data;
      }
    } catch (error) {
      errors.push('Status check failed');
    }

    try {
      const metricsResult = await this.getMetrics();
      if (metricsResult.error) {
        errors.push(`Metrics check: ${metricsResult.error.error}`);
      } else {
        metrics = metricsResult.data;
      }
    } catch (error) {
      errors.push('Metrics check failed');
    }

    return {
      available: errors.length === 0,
      health,
      status,
      metrics,
      errors,
    };
  }
}

// Create singleton instance
export const apiClient = new APIClient();

// Export convenience functions
export const {
  getHealth,
  getStatus,
  getMetrics,
  analyzeProject,
  routeAIModel,
  getSuggestions,
  checkAvailability,
  getStatusSummary,
} = apiClient;